import {
  Flex,
  Link,
  Text,
} from '@chakra-ui/react';
import {
  usePavementResurfacingAsphalt,
  usePavementResurfacingChipseal,
} from '@src/modules/performance/context/reports-api-hooks';
import LineBarChart from '@src/modules/performance/components/charts/LineBarChart';
import { CHART_COLOURS } from '@src/modules/performance/shared/constants';
import ReportSection from './ReportSection';

const informationSource = {
  title: 'Road Maintenance Activities (Pavement Resurfacing)',
  content: [
    {
      title: 'Pavement resurfacing - asphalt (lane km)',
      description:
  <>
    <Text fontSize="inherit" mb={2}>A comparison between the planned/forecast length of asphalt resurfacing renewals and the actual reported lengths achieved.</Text>
    <Text fontSize="inherit" mb={2}>Note: The asphalt resurfacing quantity is recorded in TIO as lane km. In the event an RCA has only populated the m2 quantity for asphalt resurfacing for a year, the chart will show no result for that year.</Text>
  </>,
      source: null,
    },
    {
      title: 'Pavement resurfacing - chipseal (lane km)',
      description:
  <>
    <Text fontSize="inherit" mb={2}>A comparison between the planned/forecast length of chipseal plus other resurfacing renewals and the actual reported lengths achieved.</Text>
    <Text fontSize="inherit" mb={2}>Note: The chipseal resurfacing quantity is not recorded in TIO as lane km. The chipseal quantity is calculated as the total Reseal quantity less the asphalt quantity, which will include other resurfacing.</Text>
  </>,
      source:
  <Text fontSize="inherit" as="span">
    Forecast and Achieved are sourced from
    {' '}
    <Link href="https://www.nzta.govt.nz/roads-and-rail/highways-information-portal/processes/transport-investment-online/" isExternal>NZTA Transport Investment Online.</Link>
  </Text>,
    },
  ],
};

function RoadMaintenanceDeliveryPavementResurfacing() {
  const { data: pavementResurfacingAsphalt, isLoading: isLoadingResurfacingAsphalt } = usePavementResurfacingAsphalt();
  const { data: pavementResurfacingChipseal, isLoading: isLoadingResurfacingChipseal } = usePavementResurfacingChipseal();
  const isLoading = isLoadingResurfacingAsphalt || isLoadingResurfacingChipseal;

  // Pavement Resurfacing Asphalt Chart
  const pavementResurfacingAsphaltCategories = [...new Set(pavementResurfacingAsphalt?.map((item) => item.Year))].sort((a, b) => a - b);
  const pavementResurfacingAsphaltData = [{
    name: 'Variance %',
    type: 'line',
    data: pavementResurfacingAsphaltCategories.map((year) => ({
      x: year,
      y: pavementResurfacingAsphalt?.find((item) => item.Year === year)?.Variance,
    })),
    color: CHART_COLOURS.grey,
  },
  {
    name: 'Forecast',
    type: 'bar',
    data: pavementResurfacingAsphaltCategories.map((year) => ({
      x: year,
      y: pavementResurfacingAsphalt?.find((item) => item.Year === year)?.Planned || null,
    })),
    color: CHART_COLOURS.blue,
  },
  {
    name: 'Achieved',
    type: 'bar',
    data: pavementResurfacingAsphaltCategories.map((year) => ({
      x: year,
      y: pavementResurfacingAsphalt?.find((item) => item.Year === year)?.Actual || null,
    })),
    color: CHART_COLOURS.red,
  },
  ];

  // Pavement Resurfacing Chipseal Chart
  const pavementResurfacingChipsealCategories = [...new Set(pavementResurfacingChipseal?.map((item) => item.Year))].sort((a, b) => a - b);
  const pavementResurfacingChipsealData = [{
    name: 'Variance %',
    type: 'line',
    data: pavementResurfacingChipsealCategories.map((year) => ({
      x: year,
      y: pavementResurfacingChipseal?.find((item) => item.Year === year)?.Variance,
    })),
    color: CHART_COLOURS.grey,
  },
  {
    name: 'Forecast',
    type: 'bar',
    data: pavementResurfacingChipsealCategories.map((year) => ({
      x: year,
      y: pavementResurfacingChipseal?.find((item) => item.Year === year)?.Planned || null,
    })),
    color: CHART_COLOURS.blue,
  },
  {
    name: 'Achieved',
    type: 'bar',
    data: pavementResurfacingChipsealCategories.map((year) => ({
      x: year,
      y: pavementResurfacingChipseal?.find((item) => item.Year === year)?.Actual || null,
    })),
    color: CHART_COLOURS.red,
  },
  ];

  // Remove Variance series if Forecast or Actual is null for all years
  if (pavementResurfacingAsphalt && pavementResurfacingAsphalt.every((item) => item.Planned === null || item.Actual === null)) {
    pavementResurfacingAsphaltData.shift();
  }
  if (pavementResurfacingChipseal && pavementResurfacingChipseal.every((item) => item.Planned === null || item.Actual === null)) {
    pavementResurfacingChipsealData.shift();
  }

  // Determine decimal points for asphalt charts based on the min value in the data
  const asphaltDecimalPoint = Math.min(...(pavementResurfacingAsphalt?.map((item) => item.Planned || item.Actual) || [])) < 1 ? 2 : 0;

  return (
    <ReportSection
      title="Road Maintenance Activities (Pavement Resurfacing)"
      isLoading={isLoading}
      lockYear={pavementResurfacingAsphalt?.[0]?.LockYear}
      informationSource={informationSource}
      links={[
        {
          label: 'ONF Sealed Road Pavement Rehabilitation & Resurfacing',
          url: '/onf/transport-outcomes/ep4',
          isExternal: false,
          userRoles: null,
        },
      ]}
    >
      <Flex flexDirection="row">
        <LineBarChart
          title="Pavement resurfacing - asphalt (lane km)"
          categories={pavementResurfacingAsphaltCategories}
          series={pavementResurfacingAsphaltData}
          width="280"
          height="280"
          leftAxisLabel="Lane km"
          rightAxisLabel="Variance %"
          lineSeriesName="Variance %"
          barSeriesName="Forecast"
          barSeriesFormatter={(y) => (typeof y !== 'undefined' && y !== null ? `${y.toFixed(asphaltDecimalPoint)}km` : '')}
          lineSeriesFormatter={(y) => (typeof y !== 'undefined' ? `${y.toFixed(asphaltDecimalPoint)}%` : '')}
        />
        <LineBarChart
          title="Pavement resurfacing - chipseal (lane km)"
          categories={pavementResurfacingChipsealCategories}
          series={pavementResurfacingChipsealData}
          width="280"
          height="280"
          leftAxisLabel="Lane km"
          rightAxisLabel="Variance %"
          lineSeriesName="Variance %"
          barSeriesName="Forecast"
          barSeriesFormatter={(y) => (typeof y !== 'undefined' && y !== null ? `${y.toFixed(0)}km` : '')}
          lineSeriesFormatter={(y) => (typeof y !== 'undefined' ? `${y.toFixed(0)}%` : '')}
        />
      </Flex>
    </ReportSection>
  );
}

export default RoadMaintenanceDeliveryPavementResurfacing;
